import { useState } from "react";
import {
  Phone,
  MessageCircle,
  Calendar,
  AlertTriangle,
  PhoneCall,
  Banknote,
  Download,
  CheckCircle2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FollowUpStatusBadge, PriorityBadge } from "./FollowUpStatusBadge";
import { CallHistoryTimeline } from "./CallHistoryTimeline";
import { InstalmentSchedule } from "./InstalmentSchedule";
import { LogFollowUpModal } from "./LogFollowUpModal";
import { PaymentCommitmentCard } from "./PaymentCommitmentCard";
import { AttributionBadge } from "./AttributionBadge";
import { RecordPaymentModal } from "@/components/sponsorship/RecordPaymentModal";
import { PaymentReceiptModal } from "@/components/payment/PaymentReceiptModal";
import { DeleteLogModal } from "./DeleteLogModal";
import {
  useFollowUpHistory,
  useDeleteFollowUpLog,
  useClearStaleFollowUp,
} from "@/api/followups/hooks";
import { useSponsorship } from "@/api/sponsorships/hooks";

export function SponsorDetailView({
  sponsor,
  onLogSuccess,
  onPaymentRecorded,
}) {
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);
  const [receiptTarget, setReceiptTarget] = useState(null);
  const [paymentContext, setPaymentContext] = useState(null);
  const [logToDelete, setLogToDelete] = useState(null);

  const handleRecordPayment = (context = {}) => {
    setPaymentContext(context);
    setShowPaymentModal(true);
  };

  const { data: historyData, isLoading: historyLoading } = useFollowUpHistory(
    sponsor?._id,
  );
  const { data: liveSponsor } = useSponsorship(sponsor?._id);
  const s = liveSponsor ?? sponsor; // prefer fresh server data, fall back to prop
  const { mutate: deleteLog, isPending: isDeleting } = useDeleteFollowUpLog();
  const { mutate: clearStale } = useClearStaleFollowUp();
  const logs = historyData?.data || [];

  const handleDeleteLog = (log) => {
    setLogToDelete(log);
  };

  if (!sponsor) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 text-center">
        <Phone className="h-10 w-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">
          Select a sponsor from the list to view details
        </p>
      </div>
    );
  }

  const remaining = s.amountPromised - s.paidAmount;
  const pct =
    s.amountPromised > 0 ? Math.min(s.paidAmount / s.amountPromised, 1) : 0;
  const followUp = s.followUp || {};

  const attributedPayments = (s?.payments || []).filter(
    (p) => p.isFollowUpAttributed,
  );
  const attributedTotal = attributedPayments.reduce(
    (sum, p) => sum + (p.amount || 0),
    0,
  );

  const handleWhatsApp = () => {
    const phone = s.sponsorPhone || s.careOf?.phone;
    if (!phone) return;
    const normalized = phone.replace(/\D/g, "");
    const msg = encodeURIComponent(
      "Assalamu Alaikum, this is a follow-up regarding your sponsorship pledge for the Ta'awun campaign.",
    );
    window.open(
      `https://web.whatsapp.com/send?phone=${normalized}&text=${msg}`,
      "whatsapp",
    );
  };

  const subtext = s.type === "Mahallu" ? s.mahalluName || "" : s.place || "";

  return (
    <div className="space-y-4 overflow-y-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-base font-semibold">{s.sponsorName}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {s.type}
            {subtext ? ` · ${subtext}` : ""}
            {s.chapter?.chapterName ? ` · ${s.chapter.chapterName}` : ""}
          </p>
        </div>
        <div className="flex gap-1.5">
          <FollowUpStatusBadge status={followUp.status} />
          <PriorityBadge nextActionDate={followUp.nextActionDate} />
        </div>
      </div>

      {/* Contact */}
      <div className="flex items-center gap-2 flex-wrap">
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Phone className="h-3 w-3" />
            <span>{s.sponsorPhone || "No phone"}</span>
          </div>
          {(s.sponsorPhone || s.careOf?.phone) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-green-600 hover:bg-green-50"
                  onClick={handleWhatsApp}
                >
                  <MessageCircle className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">WhatsApp</TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>
      </div>

      <Separator />

      {/* Financial Block */}
      <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Pledged</span>
          <span className="font-semibold tabular-nums">
            ₹{s.amountPromised?.toLocaleString()}
          </span>
        </div>
        <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500",
              pct >= 1
                ? "bg-emerald-500"
                : pct > 0
                  ? "bg-primary"
                  : "bg-muted-foreground/20",
            )}
            style={{ width: `${pct * 100}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Collected</span>
          <span className="font-semibold tabular-nums text-emerald-600">
            ₹{s.paidAmount?.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Remaining</span>
          <span className="font-semibold tabular-nums text-amber-600">
            ₹{remaining.toLocaleString()}
          </span>
        </div>
        {attributedTotal > 0 && (
          <div className="flex items-center justify-between text-xs border-t border-border/40 pt-2 mt-1">
            <span className="flex items-center gap-1 text-blue-600">
              <PhoneCall className="h-3 w-3" />
              Collected via CRM
            </span>
            <span className="font-semibold tabular-nums text-blue-600">
              ₹{attributedTotal.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* Follow-up Info */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3 w-3 text-muted-foreground shrink-0" />
          <div>
            <p className="text-muted-foreground">Next Action</p>
            <p className="font-medium">
              {followUp.nextActionDate
                ? new Date(followUp.nextActionDate).toLocaleDateString(
                    "en-IN",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    },
                  )
                : "Not Set"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3 text-muted-foreground shrink-0" />
          <div>
            <p className="text-muted-foreground">Failed Attempts</p>
            <p className="font-medium">{followUp.failedAttempts || 0}</p>
          </div>
        </div>
      </div>

      {followUp.assignedTo && (
        <p className="text-[10px] text-muted-foreground">
          Assigned to: {followUp.assignedTo.name || followUp.assignedTo.email}
        </p>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button
          className="flex-1"
          size="sm"
          onClick={() => setLogModalOpen(true)}
        >
          Log Follow-Up →
        </Button>
        {(s.sponsorPhone || s.careOf?.phone) && (
          <Button
            variant="outline"
            size="sm"
            className="text-green-600 hover:bg-green-50"
            onClick={handleWhatsApp}
          >
            <MessageCircle className="h-3 w-3 mr-1" />
            WhatsApp
          </Button>
        )}
        {remaining > 0 && (
          <button
            onClick={() => handleRecordPayment()}
            className="flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors border border-emerald-200"
          >
            <Banknote className="h-3 w-3" />
            Record Payment
          </button>
        )}
      </div>

      <Separator />

      {/* Payment Commitment Card */}
      {s?.followUp?.paymentCommitment?.committedDate && (
        <PaymentCommitmentCard
          commitment={s.followUp.paymentCommitment}
          onRecordPayment={handleRecordPayment}
          onClear={() =>
            clearStale({ sponsorshipId: s._id, clearCommitment: true })
          }
        />
      )}

      {/* Instalment Schedule */}
      {s?.followUp?.instalmentPlan?.instalments?.length > 0 && (
        <InstalmentSchedule
          plan={s.followUp.instalmentPlan}
          onRecordPayment={handleRecordPayment}
          onClear={() => clearStale({ sponsorshipId: s._id, clearPlan: true })}
          onDownloadReceipt={(paymentId) => {
            const p = s?.payments?.find(
              (pay) => pay._id?.toString() === paymentId?.toString(),
            );
            if (p)
              setReceiptTarget({
                paymentId: p.paymentId,
                name: s.sponsorName,
                place: s.mahalluName || s.place || "",
                amount: p.amount,
                currency: s.currency || "INR",
                createdAt: p.createdAt,
              });
          }}
        />
      )}

      {/* Post-payment banner */}
      {lastPayment && (
        <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-emerald-700">
              ₹{lastPayment.amount?.toLocaleString()} recorded
              {lastPayment.isFollowUpAttributed && " · Via Follow-Up"}
            </p>
            <p className="text-[10px] text-emerald-600 font-mono">
              #{lastPayment.paymentId}
            </p>
          </div>
          <button
            onClick={() =>
              setReceiptTarget({
                paymentId: lastPayment.paymentId,
                name: s.sponsorName,
                place: s.mahalluName || s.place || "",
                amount: lastPayment.amount,
                currency: s.currency || "INR",
                createdAt: lastPayment.createdAt,
              })
            }
            className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors shrink-0"
          >
            <Download className="h-3 w-3" />
            Receipt
          </button>
          <button
            onClick={() => setLastPayment(null)}
            className="text-emerald-400 hover:text-emerald-600 shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Payments Received */}
      {s?.payments?.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-1">
            Payments Received
          </p>
          {s.payments
            .slice()
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((p) => (
              <div
                key={p._id}
                className="flex items-center gap-3 rounded-xl border border-border/50 bg-background px-3 py-2.5"
              >
                <div
                  className={cn(
                    "w-2 h-2 rounded-full shrink-0",
                    p.isFollowUpAttributed
                      ? "bg-blue-500"
                      : "bg-muted-foreground/30",
                  )}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold tabular-nums text-emerald-700">
                    +₹{p.amount?.toLocaleString()}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(p.createdAt).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                    {" · "}
                    <span className="font-mono">#{p.paymentId}</span>
                  </p>
                </div>
                {p.isFollowUpAttributed && (
                  <AttributionBadge className="shrink-0" />
                )}
                <button
                  onClick={() =>
                    setReceiptTarget({
                      paymentId: p.paymentId,
                      name: s.sponsorName,
                      place: s.mahalluName || s.place || "",
                      amount: p.amount,
                      currency: s.currency || "INR",
                      createdAt: p.createdAt,
                    })
                  }
                  className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-muted transition-colors shrink-0"
                  title="Download receipt"
                >
                  <Download className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </div>
            ))}
        </div>
      )}

      {/* Call History */}
      <div>
        <h3 className="text-xs font-semibold mb-3">Call History</h3>
        <CallHistoryTimeline
          logs={logs}
          payments={s?.payments || []}
          instalmentPlan={s?.followUp?.instalmentPlan}
          isLoading={historyLoading}
          onDeleteLog={handleDeleteLog}
        />
      </div>

      {/* Log Modal */}
      <LogFollowUpModal
        open={logModalOpen}
        onOpenChange={setLogModalOpen}
        sponsorshipId={sponsor._id}
        sponsor={s}
        onLogSuccess={onLogSuccess}
      />

      {/* Record Payment Modal */}
      {showPaymentModal && (
        <RecordPaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentContext(null);
          }}
          sponsorship={s}
          suggestedAmount={paymentContext?.suggestedAmount}
          contextLabel={paymentContext?.label}
          onSuccess={(payment) => {
            setShowPaymentModal(false);
            setPaymentContext(null);
            setLastPayment(payment);
            onPaymentRecorded?.(payment);
          }}
        />
      )}

      {/* Receipt Modal */}
      {receiptTarget && (
        <PaymentReceiptModal
          isOpen={!!receiptTarget}
          onClose={() => setReceiptTarget(null)}
          payment={receiptTarget}
        />
      )}

      {/* Delete Log Modal */}
      {logToDelete && (
        <DeleteLogModal
          log={logToDelete}
          sponsor={s}
          isOpen={!!logToDelete}
          onClose={() => setLogToDelete(null)}
          isLoading={isDeleting}
          onConfirm={(payload) => {
            deleteLog(payload, {
              onSuccess: () => setLogToDelete(null),
            });
          }}
        />
      )}
    </div>
  );
}
